import React, { useEffect, useState, useContext, useRef } from "react";
import moment from "moment";
import { InputLabel } from "@mui/material";
import { Card, CardBody, Col, Row } from "reactstrap";

import CustomDatePicker from "views/pages/manage/common/commonComponents/CustomDatePicker";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import { isFilterApplied } from "views/pages/manage/edpOps/constant/batch-search";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";

import { FILTER_MENU, ReceiptListContext } from "./context";
import {
  InputLabelStyle,
  getCourseIdDropdown,
  getDropdowns,
} from "./constants";

const FilterMenu = ({ businessAreaOptions, closeForm, applyFilters }) => {
  const hasMounted = useRef(false);
  const {
    globalFilter,
    dropdownOptions,
    setDropdownOptions,
    filterCount,
    setFilterCount,
  } = useContext(ReceiptListContext);

  const [filterForm, setFilterForm] = useState(prefillFormFields());
  const [courseIdDropdown, setCourseIdDropdown] = useState(
    globalFilter.courseIdList.length ? globalFilter.courseIdList : []
  );

  function prefillFormFields() {
    return {
      businessAreaList: globalFilter.businessAreaList,
      courseTypeList: globalFilter.courseTypeList,
      receiptTypeList: globalFilter.receiptTypeList,
      courseIdList: globalFilter.courseIdList,
      termList: globalFilter.termList,
      startDate: globalFilter.startDate,
      endDate: globalFilter.endDate,
    };
  }

  const {
    businessAreaList,
    courseTypeList,
    receiptTypeList,
    termList,
    courseIdList,
    startDate,
    endDate,
  } = filterForm; // destructuring

  useEffect(() => {
    const fetchDropdown = async () => {
      const response = await getDropdowns();
      setDropdownOptions(response);
    };

    if (!dropdownOptions.courseTypeList.length) {
      // call API
      fetchDropdown();
    }
  }, []);

  useEffect(() => {
    if (hasMounted.current && termList.length && businessAreaList.length) {
      // call courseId API
      getCourseIds();
      filterFormHandler([], "courseIdList");
    }

    if (!termList.length || !businessAreaList.length) {
      //  reset `courseIdList` field
      filterFormHandler([], "courseIdList");
    }

    if (!hasMounted.current) {
      hasMounted.current = true;
    }

    async function getCourseIds() {
      const response = await getCourseIdDropdown({
        businessAreaIds: businessAreaList.map((bu) => bu.businessAreaId),
        academicTermIds: termList.map((term) => term.id),
        courseCategoryType: courseTypeList.map(
          (courseType) => courseType.value
        ),
      });

      setCourseIdDropdown(response);
    }
  }, [termList, businessAreaList, courseTypeList]);

  const filterFormHandler = (value, key) => {
    setFilterForm((prev) => ({ ...prev, [key]: value ?? [] }));
  };

  const resetFilterForm = () => {
    closeForm();
    setFilterForm(FILTER_MENU);
    applyFilters(FILTER_MENU);

    setFilterCount(0);
  };

  const handleApplyFilter = () => {
    closeForm();

    const filterCount = isFilterApplied({
      ...filterForm,
    });
    setFilterCount(filterCount);

    applyFilters(filterForm);
  };

  return (
    <Card
      className="mb-0 regular-body label-color"
      style={{ width: "330px", borderRadius: "12px" }}
    >
      <CardBody className="d-flex flex-column">
        <div className="text-bold text-lg mb-2">Filters</div>

        <div className="d-flex flex-column gap-md">
          <AAutoComplete
            // height={inputHeight}
            label="Business Area"
            placeholder="Select max 10 Business Area"
            items={businessAreaOptions}
            currentValue={businessAreaList}
            isShowCount={true}
            handleChange={(value) => {
              // max 10 BU rows can be selected for Search receipts
              if (value.length > 10)
                filterFormHandler(value.slice(0, 10), "businessAreaList");
              else filterFormHandler(value, "businessAreaList");
            }}
            inputKey="businessAreaList"
          />

          <AAutoComplete
            // height={inputHeight}
            label="Course Type"
            items={dropdownOptions.courseTypeList}
            currentValue={courseTypeList}
            isShowCount={true}
            handleChange={(value) => {
              filterFormHandler(value, "courseTypeList");
            }}
            inputKey="courseTypeList"
          />

          <AAutoComplete
            // height={inputHeight}
            label="Receipt Type"
            // placeholder="Batch"
            items={dropdownOptions.receiptTypeList}
            currentValue={receiptTypeList}
            isShowCount={true}
            handleChange={(value) => {
              filterFormHandler(value, "receiptTypeList");
            }}
            inputKey="receiptTypeList"
          />

          <AAutoComplete
            // height={inputHeight}
            label="Term"
            // placeholder="Batch"
            items={dropdownOptions.termList}
            currentValue={termList}
            isShowCount={true}
            handleChange={(value) => {
              filterFormHandler(value, "termList");
            }}
            inputKey="termList"
          />

          <AAutoComplete
            isDisabled={!termList.length || !businessAreaList.length}
            label="Course ID"
            // placeholder="Batch"
            items={courseIdDropdown}
            currentValue={courseIdList}
            isShowCount={true}
            handleChange={(value) => {
              filterFormHandler(value, "courseIdList");
            }}
            inputKey="courseIdList"
          />

          <div>
            <InputLabel style={InputLabelStyle}>Receipt Date</InputLabel>
            <Row>
              <Col sm="6">
                <CustomDatePicker
                  placeHolder="From"
                  // minDate={moment()}
                  value={startDate ? moment(startDate) : null}
                  handler={(index, date, keyName) => {
                    filterFormHandler(date, "startDate");

                    const oneWeekDiff = moment.min([
                      new moment(date)?.add(7, "day"),
                      moment().startOf("day"),
                    ]);
                    filterFormHandler(oneWeekDiff, "endDate");
                  }}
                  maxDate={moment()}
                />
              </Col>
              <Col sm="6">
                <CustomDatePicker
                  placeHolder="To"
                  value={endDate ? moment(endDate) : null}
                  handler={(index, date, keyName) => {
                    filterFormHandler(date, "endDate");
                  }}
                  minDate={moment(startDate)}
                  maxDate={moment.min([
                    new moment(startDate)?.add(7, "day"),
                    moment().startOf("day"),
                  ])}
                  disabled={!startDate}
                />
              </Col>
            </Row>
          </div>

          <div className="mt-3 d-flex flex-row-reverse justify-content-between">
            <AButton
              variant="primary_filled"
              className="w-full"
              onClick={handleApplyFilter}
              disabled={
                !businessAreaList.length &&
                !courseTypeList.length &&
                !receiptTypeList.length &&
                !courseIdList.length &&
                !termList.length &&
                !startDate &&
                !endDate
              }
            >
              Apply filter
            </AButton>

            {filterCount > 0 && (
              <AButton
                variant="outline"
                className="button_remove_left_margin"
                updatedStyle={{ border: "1px solid #CC5F5F", color: "#CC5F5F" }}
                onClick={resetFilterForm}
              >
                Remove filter
              </AButton>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default FilterMenu;
